import { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

/**
 * The async player's fullscreen, for whatever wraps a LessonPreview: the
 * delivered lesson (LessonEditPreview) and the coach's match on the lesson
 * page (ChapteredMatch). Put `containerRef` on the wrapper, give LessonPreview
 * `isFullscreen` and `onFullscreenToggle={toggle}`, and style the wrapper by
 * `isFullscreen` ('fixed inset-0 z-50 bg-black flex flex-col' when expanded).
 *
 * Real fullscreen where the API exists; the CSS overlay where it does not.
 *
 * Both paths drive the same `isFullscreen` flag, so nothing downstream has to
 * know which one is in play — the wrapper's className, LessonPreview's icon
 * and its label colours all read one boolean.
 *
 * Capability, not user agent: iPhone Safari simply has no
 * Element.requestFullscreen, so the check is for the method itself plus
 * document.fullscreenEnabled (which is false inside an iframe that has not
 * been granted the permission). iPad reports the method and takes the real
 * path; iPhone falls through to the overlay.
 *
 * `webkitEnterFullscreen` is deliberately not used on iPhone: it hands the
 * <video> to Apple's player, whose scrubber shows the current clip's duration
 * rather than the lesson's, so a seven-clip lesson would present as a
 * forty-second video.
 */
export function useFullscreen<T extends HTMLElement = HTMLDivElement>() {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<T>(null);

  const toggle = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;

    const canNative =
      typeof el.requestFullscreen === 'function' && document.fullscreenEnabled;

    if (!canNative) {
      setIsFullscreen((v) => !v);
      return;
    }

    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => {});
      return;
    }

    /* flushSync, and this is the load-bearing line. The wrapper is
       `display: contents` when not expanded, so it generates no box and there
       is nothing for the browser to fullscreen. Flushing the state first gives
       it the overlay's box synchronously, and because flushSync runs inside
       this click handler the user activation that requestFullscreen requires is
       still live. Requesting from an effect keyed on the state change instead
       is the shape that rejects with NotAllowedError — the most likely reason
       the original attempts failed. */
    flushSync(() => setIsFullscreen(true));
    el.requestFullscreen().catch(() => {
      /* Rejected for any reason: keep the overlay rather than leaving the
         control looking broken. */
    });
  }, []);

  /* document.fullscreenElement is the source of truth once the real path is in
     use, so Escape, the browser's own exit button and F11 all land here without
     special casing. Ignored while the overlay path is running, where no
     fullscreenchange event ever fires. */
  useEffect(() => {
    const onChange = () => {
      if (!containerRef.current) return;
      if (document.fullscreenElement === containerRef.current) setIsFullscreen(true);
      else if (document.fullscreenElement === null) setIsFullscreen(false);
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  return { containerRef, isFullscreen, toggle };
}
